import React, { useEffect, useMemo, useRef } from 'react'

import { getDiscoveryPanel } from '@disco/state-management/selectors/client-libraries'
import { sendDiscoverMixpanelEvent } from '@disco/utils/mixpanel/discover-page'

import type { ClientLibraryId } from 'types/client-library'

import emptyArtists1x from 'assets/images/empty-artists.png'
import emptyArtists2x from 'assets/images/empty-artists@2x.png'
import emptySearch1x from 'assets/images/empty-search.png'
import emptySearch2x from 'assets/images/empty-search@2x.png'

import LibraryGridItem from 'components/common/library-grid-item'
import { Grid as DSGrid } from 'components/ds'
import EmptyState from 'components/ui/empty-state'
import InfiniteScroll from 'components/ui/infinite-scroll'

import { useSelector } from 'hooks/use-redux'

import { getCatalogBusinessNameFromUrl } from '../utils'
import type { FilterValue } from './../BrowseLibraries'

type Props = {
  hideLoadingLabel?: boolean
  isSearching: boolean
  query: string | null | undefined
  filter: FilterValue
  loadNext: (...args: Array<any>) => any
  libraryGridItemType?: 'shadow'
  easyAccess?: boolean
  setVisibleSearchResults?: (count: number) => void
}

const AllDiscoveryLibraries = ({
  hideLoadingLabel,
  isSearching,
  query,
  filter,
  loadNext,
  libraryGridItemType,
  easyAccess,
  setVisibleSearchResults,
}: Props): React.ReactNode => {
  const discoveryPanel = useSelector(getDiscoveryPanel)
  const prevResultsCountRef = useRef<number | undefined>(undefined)

  // Hoist list computation above hooks and early returns so it's always available
  const baseList = useMemo(
    () =>
      discoveryPanel?.list?.filter(
        (libraryId: ClientLibraryId) =>
          discoveryPanel.items[libraryId]?.display_name,
      ) ?? [],
    [discoveryPanel],
  )

  // Apply easyAccess filter locally when a specific filter (like 'is_favorite') is active
  // This allows combining easyAccess with other filters
  const list = useMemo(() => {
    return easyAccess
      ? baseList?.filter(
          (libraryId: ClientLibraryId) =>
            discoveryPanel.items[libraryId]?.status === 'approved',
        )
      : baseList
  }, [easyAccess, baseList, discoveryPanel.items])

  useEffect(() => {
    if (
      discoveryPanel.loaded &&
      !discoveryPanel.loading &&
      !discoveryPanel.next
    ) {
      prevResultsCountRef.current = list?.length ?? 0
    }
  }, [list, discoveryPanel.loaded, discoveryPanel.loading, discoveryPanel.next])

  useEffect(() => {
    if (
      list?.length === prevResultsCountRef.current &&
      !isSearching &&
      setVisibleSearchResults
    ) {
      setVisibleSearchResults(list.length)
    }
  }, [list.length, isSearching, setVisibleSearchResults])

  if (discoveryPanel.loaded && discoveryPanel.list.length === 0) {
    if (!isSearching && query === '' && !filter) {
      return (
        <EmptyState>
          <img
            alt=""
            srcSet={`${emptyArtists1x}, ${emptyArtists2x} 2x`}
            width="194"
            height="185"
          />
          <p>No Catalogs here yet.</p>
        </EmptyState>
      )
    } else {
      return (
        <EmptyState>
          <img
            alt=""
            srcSet={`${emptySearch1x}, ${emptySearch2x} 2x`}
            width="177"
            height="160"
          />
          <p>Sorry! No results for that search.</p>
        </EmptyState>
      )
    }
  } else {
    const gridItems = list?.map((libraryId: ClientLibraryId) => {
      const businessName = getCatalogBusinessNameFromUrl(
        discoveryPanel.items[libraryId]?.public_url || '',
      )

      return (
        <LibraryGridItem
          key={libraryId}
          lib={discoveryPanel.items[libraryId]}
          type={libraryGridItemType}
          onClick={() => {
            sendDiscoverMixpanelEvent('Click Image', {
              context: 'Search Results',
              catalogId: libraryId,
              catalogBusiness: businessName || 'Unknown',
              catalogName:
                discoveryPanel.items[libraryId]?.display_name ||
                'Untitled Catalog',
            })
          }}
        />
      )
    })

    return (
      <InfiniteScroll
        next={discoveryPanel.next}
        loadNext={loadNext}
        hideLoadingLabel={hideLoadingLabel}
      >
        <DSGrid columns={{ initial: '1', sm: '2', md: '3' }} gap="4">
          {gridItems}
        </DSGrid>
      </InfiniteScroll>
    )
  }
}

export default AllDiscoveryLibraries
